import type { GateLabels } from "@nexseed/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  redirect,
  useBlocker,
  useNavigate,
} from "@tanstack/react-router";
import { ChevronDown, Columns3, Plus, ShieldCheck } from "lucide-react";
import { useMemo, useState, useSyncExternalStore } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { ConfirmDialog } from "@/components/app/dialogs";
import { EmptyState } from "@/components/app/empty-state";
import { PageHeader } from "@/components/app/page-header";
import { Button } from "@/components/ui/button";
import { Callout } from "@/components/ui/callout";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { adminApi, adminKeys, type AdminRole } from "@/features/admin/api";
import { RoleColumnHead } from "@/features/admin/components/access-matrix";
import { ChangesBar } from "@/features/admin/components/changes-bar";
import { ModulesOverview } from "@/features/admin/components/modules-overview";
import { ReviewView } from "@/features/admin/components/review-view";
import { RolesRoster } from "@/features/admin/components/roles-roster";
import {
  FormsLegend,
  FormsMatrix,
} from "@/features/admin/components/forms-matrix";
import { FORM_ACTIONS, type FormAction } from "@/features/admin/helpers";
import { NewRoleDialog } from "@/features/admin/components/new-role-dialog";
import { RoleWorkspace } from "@/features/admin/components/role-workspace";
import { FORM_LIST, formModule } from "@/features/admin/module-place";
import { moduleControls, plantsText } from "@/features/admin/reach";
import { reviewPairs } from "@/features/admin/review-model";
import {
  cleanDraft,
  draftNotes,
  sectionsOf,
  type RoleDraft,
} from "@/features/admin/role-model";
import { ROLE_DEVICES } from "@/features/admin/api";
import { useStagedGrid, type GridBase } from "@/features/admin/staged-grid";
import { codesUnder, nest, type TreeNode } from "@/features/admin/tree";
import { RecordHistorySheet } from "@/features/audit/components/record-history";
import { useAuth } from "@/features/auth/store";
import { ApiError } from "@/lib/api-client";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/admin/roles")({
  validateSearch: z.object({
    // .catch: an old link to a removed view (Compare permissions) lands on Roles.
    view: z.enum(["roles", "modules", "role", "forms"]).catch("roles"),
    role: z.number().int().positive().optional(),
  }),
  // roles.manage on its own: someone may be trusted to shape permissions without onboarding people,
  // and someone may onboard without being trusted to change what a role grants.
  beforeLoad: () => {
    if (!useAuth.getState().can("roles.manage")) throw redirect({ to: "/" });
  },
  component: Roles,
});

/** Blank names mean "the standard name", so they are dropped from what is saved. */
const cleanLabels = (l: GateLabels): GateLabels =>
  Object.fromEntries(
    Object.entries(l)
      .map(([k, v]) => [k, v?.trim() ?? ""])
      .filter(([, v]) => v)
  ) as GateLabels;

const errText = (e: unknown, fallback: string) =>
  e instanceof ApiError ? e.message : fallback;
const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

const NARROW = "(max-width: 767px)";
function useNarrow() {
  return useSyncExternalStore(
    (cb) => {
      const m = window.matchMedia(NARROW);
      m.addEventListener("change", cb);
      return () => m.removeEventListener("change", cb);
    },
    () => window.matchMedia(NARROW).matches,
    () => false
  );
}

interface FlatNode {
  node: TreeNode;
  depth: number;
  path: string;
  codes: string[];
  /** The module (top-level node) it sits in. */
  root: string;
}

/** Granting these will one day ask for an e-mailed code (D4 is drawn, not yet enforced). */
const SENSITIVE = [
  "audit.purge",
  "document.cancel",
  "roles.manage",
  "users.manage",
  "access_all_facilities",
];

const formKey = (form: string, action: FormAction) => `${form}.${action}`;

function Roles() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { view, role: roleParam } = Route.useSearch();
  const narrow = useNarrow();

  const roles = useQuery({
    queryKey: adminKeys.roles,
    queryFn: adminApi.roles,
  });
  const tree = useQuery({ queryKey: adminKeys.tree, queryFn: adminApi.tree });
  const summary = useQuery({
    queryKey: adminKeys.summary,
    queryFn: adminApi.summary,
  });
  const roleList = useMemo(() => roles.data?.items ?? [], [roles.data]);
  const nested = useMemo(
    () => nest((tree.data?.items ?? []).filter((n) => n.isEnabled)),
    [tree.data]
  );

  const [compare, setCompare] = useState<Set<number>>(new Set());
  const [mobileRole, setMobileRole] = useState<number | null>(null);
  const [review, setReview] = useState(false);
  // Unsaved changes to each role's own settings (name, device, gate names and tabs, manual mode).
  const [drafts, setDrafts] = useState<ReadonlyMap<number, RoleDraft>>(
    () => new Map()
  );
  const [history, setHistory] = useState<AdminRole | null>(null);
  const [removing, setRemoving] = useState<AdminRole | null>(null);
  const [creating, setCreating] = useState<{ from: AdminRole | null } | null>(
    null
  );

  // Two staged grids over the same roles: permission codes, and form actions.
  const permBase: GridBase = useMemo(
    () => new Map(roleList.map((r) => [String(r.id), new Set(r.permissions)])),
    [roleList]
  );
  const formBase: GridBase = useMemo(
    () =>
      new Map(
        roleList.map((r) => [
          String(r.id),
          new Set(
            r.forms.flatMap((f) =>
              FORM_ACTIONS.filter(([k]) => f[k]).map(([k]) =>
                formKey(f.formCode, k)
              )
            )
          ),
        ])
      ),
    [roleList]
  );
  const perms = useStagedGrid(permBase);
  const forms = useStagedGrid(formBase);
  const sections = useMemo(() => sectionsOf(nested), [nested]);
  const selected =
    roleList.find((r) => r.id === roleParam) ?? roleList[0] ?? null;
  const selectRole = (id: number) =>
    void nav({ to: ".", search: { view: "role", role: id }, replace: true });
  /** Open a role in the editor, scrolled to one module's card. */
  const openRole = (id: number, module?: string) => {
    void nav({ to: ".", search: { view: "role", role: id } });
    if (module)
      window.setTimeout(
        () =>
          document
            .getElementById(`mod-${module}`)
            ?.scrollIntoView({ behavior: "smooth", block: "start" }),
        80
      );
  };
  const modules = useMemo(() => moduleControls(sections), [sections]);
  const onDraft = (id: number, patch: RoleDraft) =>
    setDrafts((prev) => {
      const r = roleList.find((x) => x.id === id);
      if (!r) return prev;
      const next = new Map(prev);
      const merged = cleanDraft(r, { ...prev.get(id), ...patch });
      // Keep a name being typed even while it matches, so the input does not jump back.
      if (patch.name !== undefined && merged.name === undefined)
        delete merged.name;
      if (Object.keys(merged).length) next.set(id, merged);
      else next.delete(id);
      return next;
    });
  const draftCount = [...drafts.entries()].reduce((n, [id, d]) => {
    const r = roleList.find((x) => x.id === id);
    return n + (r ? draftNotes(r, d).length : 0);
  }, 0);
  const pendingOf = (id: number) => {
    const k = String(id);
    const p = perms.diff.find((d) => d.row === k);
    const f = forms.diff.find((d) => d.row === k);
    const r = roleList.find((x) => x.id === id);
    const d = drafts.get(id);
    return (
      (p ? p.add.length + p.remove.length : 0) +
      (f ? f.add.length + f.remove.length : 0) +
      (r && d ? draftNotes(r, d).length : 0)
    );
  };
  const changeCount = perms.count + forms.count + draftCount;
  const dirty = changeCount > 0;

  useBlocker({
    shouldBlockFn: ({ next }) =>
      next.pathname !== "/admin/roles" &&
      dirty &&
      !window.confirm("Leave without saving the permission changes?"),
    enableBeforeUnload: () => dirty,
  });

  // The tree flattened in display order, each node with its full path and the codes beneath it.
  const flat = useMemo(() => {
    const out: FlatNode[] = [];
    const walk = (
      nodes: TreeNode[],
      depth: number,
      trail: string[],
      root: string
    ) => {
      for (const n of nodes) {
        const codes = codesUnder(n);
        // A heading with nothing to grant under it is left out rather than drawn as a dead row.
        if (!codes.length) continue;
        const path = [...trail, n.label].join(" › ");
        const r = depth === 0 ? n.code : root;
        out.push({ node: n, depth, path, codes, root: r });
        walk(n.children, depth + 1, [...trail, n.label], r);
      }
    };
    walk(nested, 0, [], "");
    return out;
  }, [nested]);
  const labelOf = useMemo(
    () =>
      new Map(
        flat
          .filter((f) => f.node.permission)
          .map((f) => [f.node.permission!, f.path])
      ),
    [flat]
  );

  const shownRoles = useMemo(() => {
    if (narrow) {
      const pick = roleList.find((r) => r.id === mobileRole) ?? roleList[0];
      return pick ? [pick] : [];
    }
    return compare.size >= 2
      ? roleList.filter((r) => compare.has(r.id))
      : roleList;
  }, [narrow, roleList, mobileRole, compare]);

  // What saving sends: each touched role's full permission list and/or its full form grants.
  const touched = [
    ...new Set([
      ...[...perms.diff, ...forms.diff].map((d) => d.row),
      ...[...drafts.keys()].map(String),
    ]),
  ];
  const roleById = new Map(roleList.map((r) => [String(r.id), r]));
  const formsOf = (roleKey: string) => {
    const on = forms.after(roleKey);
    return FORM_LIST.filter((f) =>
      FORM_ACTIONS.some(([k]) => on.has(formKey(f.code, k)))
    ).map((f) => ({
      formCode: f.code,
      canView: on.has(formKey(f.code, "canView")),
      canCreate: on.has(formKey(f.code, "canCreate")),
      canEdit: on.has(formKey(f.code, "canEdit")),
      canSubmit: on.has(formKey(f.code, "canSubmit")),
      canCancel: on.has(formKey(f.code, "canCancel")),
    }));
  };

  const save = useMutation({
    mutationFn: () =>
      adminApi.saveRoleMatrix(
        touched.map((key) => {
          const d = drafts.get(Number(key)) ?? {};
          return {
            id: Number(key),
            permissions: perms.diff.some((r) => r.row === key)
              ? [...perms.after(key)]
              : undefined,
            forms: forms.diff.some((r) => r.row === key)
              ? formsOf(key)
              : undefined,
            name: d.name,
            clients: d.device ? [...ROLE_DEVICES[d.device].clients] : undefined,
            gateLabels: d.gateLabels ? cleanLabels(d.gateLabels) : undefined,
            passInManualMode: d.passInManualMode,
          };
        })
      ),
    onSuccess: async () => {
      toast.success(
        `Saved ${plural(touched.length, "role", "roles")}. Anyone holding them re-authenticates on their next request.`
      );
      setReview(false);
      // Clear the staged marks only once the saved grants are back, so no cell flashes.
      await qc.invalidateQueries({ queryKey: ["admin"] });
      perms.discard();
      forms.discard();
      setDrafts(new Map());
    },
  });

  const loading = roles.isLoading || tree.isLoading;
  const error = roles.error ?? tree.error;

  // Unsaved changes per module, for the Modules cards.
  const pendingIn = (module: string) => {
    const m = modules.find((x) => x.code === module);
    if (!m) return 0;
    const codes = perms.diff.flatMap((d) => [...d.add, ...d.remove]);
    const formsTouched = forms.diff.flatMap((d) =>
      [...d.add, ...d.remove].map((k) => k.slice(0, k.lastIndexOf(".")))
    );
    return (
      codes.filter((c) => m.controls.includes(c)).length +
      new Set(formsTouched.filter((f) => formModule(f) === module)).size
    );
  };
  const plantsOf = (id: number) =>
    plantsText(summary.data?.rolePlants.find((r) => r.roleId === id)?.plants);

  const pairs = review
    ? reviewPairs({
        roles: roleList,
        sections,
        modules,
        permsBefore: (k) => permBase.get(k) ?? new Set(),
        permsAfter: (k) => perms.after(k),
        formsBefore: (k) => formBase.get(k) ?? new Set(),
        formsAfter: (k) => forms.after(k),
        drafts,
        touched,
      })
    : [];
  const people = touched.reduce(
    (n, k) => n + (roleById.get(k)?.userCount ?? 0),
    0
  );
  const sensitive = (() => {
    for (const d of perms.diff)
      for (const c of d.add)
        if (SENSITIVE.includes(c)) {
          const r = roleById.get(d.row);
          return {
            label: labelOf.get(c)?.split(" › ").pop() ?? c,
            who: `${r?.name ?? "Role"} · ${plural(r?.userCount ?? 0, "person", "people")}`,
            code: c,
          };
        }
    return null;
  })();

  return (
    <>
      <PageHeader
        title="Roles & permissions"
        description="Everything about a module is in its card: permissions, forms, tabs and settings. Nothing saves until you review the changes."
        actions={
          <Button onClick={() => setCreating({ from: null })}>
            <Plus className="h-4 w-4" /> New role
          </Button>
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Tabs
          value={view}
          onValueChange={(v) => {
            setReview(false);
            void nav({
              to: ".",
              // The tabs hand back a plain string; they only ever carry this page's own views.
              search: { view: v as typeof view, role: roleParam },
              replace: true,
            });
          }}
        >
          <TabsList className="mb-0">
            <TabsTrigger value="roles">Roles</TabsTrigger>
            <TabsTrigger value="modules">Modules</TabsTrigger>
            <TabsTrigger value="role">Edit a role</TabsTrigger>
            <TabsTrigger value="forms">Compare forms</TabsTrigger>
          </TabsList>
        </Tabs>
        {view === "role" ||
        view === "roles" ||
        view === "modules" ? null : narrow ? (
          <Select
            aria-label="Role"
            value={String(shownRoles[0]?.id ?? "")}
            onChange={(e) => setMobileRole(Number(e.target.value))}
            className="flex-1"
          >
            {roleList.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </Select>
        ) : (
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" size="sm">
                  <Columns3 className="h-4 w-4" />
                  {compare.size >= 2
                    ? `Comparing ${compare.size}`
                    : "Compare roles"}
                  <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="max-h-96 overflow-y-auto"
              >
                <DropdownMenuLabel>
                  Show only these roles (pick two or more)
                </DropdownMenuLabel>
                {roleList.map((r) => (
                  <DropdownMenuCheckboxItem
                    key={r.id}
                    checked={compare.has(r.id)}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={(c) =>
                      setCompare((prev) => {
                        const next = new Set(prev);
                        if (c) next.add(r.id);
                        else next.delete(r.id);
                        return next;
                      })
                    }
                  >
                    {r.name}
                  </DropdownMenuCheckboxItem>
                ))}
                {compare.size > 0 && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={() => setCompare(new Set())}>
                      Show every role
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
      </div>

      {view === "forms" && (
        <Callout
          className="mb-3"
          title="Forms narrow what the permissions allow"
        >
          A role with nothing ticked here is not narrowed: its permissions apply
          on every form, except the Drying, Processing, Processing &amp; Packing
          and Lot Blending slips, which a role works only where they are ticked.
          Tick anything and that role may act only where it is ticked.
        </Callout>
      )}

      {review ? (
        <ReviewView
          pairs={pairs}
          title="Before and after"
          description={`One module, one role at a time. Everyone holding these roles (${plural(people, "person", "people")}) gets the new access on their next request.`}
          publishLabel={`Publish ${plural(changeCount, "change", "changes")}`}
          publishing={save.isPending}
          error={
            save.error
              ? errText(save.error, "Could not save the changes")
              : null
          }
          onBack={() => setReview(false)}
          onPublish={() => save.mutate()}
          sensitive={sensitive}
        />
      ) : (view === "roles" || view === "modules") && !error && !loading ? (
        view === "roles" ? (
          <RolesRoster
            roles={roleList}
            sections={sections}
            summary={summary.data}
            pendingOf={pendingOf}
            onOpen={openRole}
          />
        ) : (
          <ModulesOverview
            roles={roleList}
            sections={sections}
            summary={summary.data}
            pending={pendingIn}
            onOpenRole={openRole}
          />
        )
      ) : view === "role" && selected && !error && !loading ? (
        <RoleWorkspace
          roles={roleList}
          sections={sections}
          formList={FORM_LIST}
          selected={selected}
          onSelect={selectRole}
          perms={perms}
          forms={forms}
          drafts={drafts}
          onDraft={onDraft}
          pendingOf={pendingOf}
          onNew={() => setCreating({ from: null })}
          onDuplicate={(r) => setCreating({ from: r })}
          onHistory={setHistory}
          onHolders={(r) =>
            void nav({ to: "/admin/users", search: { roleId: r.id } })
          }
          onDelete={setRemoving}
          plantsOf={plantsOf}
        />
      ) : (
        <section className="relative overflow-hidden rounded-2xl border border-border bg-surface-raised shadow-card">
          {error ? (
            <div className="p-4">
              <Callout
                tone="danger"
                title="Roles could not be loaded"
                action={
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      void roles.refetch();
                      void tree.refetch();
                    }}
                  >
                    Retry
                  </Button>
                }
              >
                {errText(error, "Try again in a moment.")}
              </Callout>
            </div>
          ) : loading ? (
            <MatrixSkeleton />
          ) : !roleList.length ? (
            <EmptyState
              icon={ShieldCheck}
              title="No roles yet."
              description="Create one, then tick what it may do."
              action={
                <Button size="sm" onClick={() => setCreating({ from: null })}>
                  <Plus className="h-4 w-4" /> New role
                </Button>
              }
            />
          ) : (
            <FormsMatrix
              forms={FORM_LIST}
              roles={shownRoles}
              head={(r) => {
                const narrowed = forms.after(String(r.id)).size > 0;
                return (
                  <RoleColumnHead
                    name={r.name}
                    meta={
                      <span
                        className={cn(
                          "rounded-full px-1.5 font-bold",
                          narrowed
                            ? "bg-warning-soft text-warning-text"
                            : "bg-neutral-soft text-neutral-text"
                        )}
                      >
                        {narrowed ? "Narrowed" : "All forms"}
                      </span>
                    }
                  />
                );
              }}
              state={(form, action, roleId) =>
                forms.state(String(roleId), formKey(form, action))
              }
              onToggle={(form, action, roleId) =>
                forms.toggle(String(roleId), formKey(form, action))
              }
            />
          )}
          <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-border bg-surface-sunken/60 px-4 py-2.5 text-xs text-ink-muted">
            <FormsLegend />
            <span className="font-medium">
              <span className="num">{labelOf.size}</span> permissions ·{" "}
              <span className="num">{roleList.length}</span> roles
            </span>
          </footer>
        </section>
      )}

      {dirty && !review && (
        <ChangesBar
          count={changeCount}
          summary={plural(touched.length, "role", "roles")}
          onDiscard={() => {
            perms.discard();
            forms.discard();
            setDrafts(new Map());
          }}
          onReview={() => {
            save.reset();
            setReview(true);
          }}
        />
      )}

      <NewRoleDialog
        open={creating !== null}
        onOpenChange={(o) => !o && setCreating(null)}
        roles={roleList}
        from={creating?.from}
      />
      {history && (
        <RecordHistorySheet
          root="roles"
          id={history.id}
          label={history.name}
          open
          onOpenChange={(o) => !o && setHistory(null)}
        />
      )}
      <ConfirmDialog
        open={removing !== null}
        title={`Delete ${removing?.name ?? ""}?`}
        description="Nobody holds it, so nothing changes for anyone. This cannot be undone."
        confirmLabel="Delete role"
        danger
        onClose={() => setRemoving(null)}
        onConfirm={async () => {
          const r = removing;
          if (!r) return;
          try {
            await adminApi.deleteRole(r.id);
            toast.success(`${r.name} deleted`);
            void qc.invalidateQueries({ queryKey: adminKeys.roles });
          } catch (e) {
            toast.error(errText(e, "Could not delete the role"));
          }
          setRemoving(null);
        }}
      />
    </>
  );
}

function MatrixSkeleton() {
  return (
    <div aria-hidden className="divide-y divide-border">
      <div className="h-16 bg-surface-header" />
      {Array.from({ length: 9 }, (_, i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-3">
          <Skeleton className={cn("h-3.5 w-48", ["", "ml-4", "ml-8"][i % 3])} />
          <div className="flex flex-1 gap-14 pl-16">
            {Array.from({ length: 8 }, (_, j) => (
              <Skeleton key={j} className="h-4 w-4 rounded-full" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
